<script setup>
import { computed, getCurrentInstance } from 'vue'

import DropdownButton from '@/components/DropdownButton.vue'
import GbButton from '@/components/GbButton.vue'
import { actionPermissions, ACTION_TYPES, CONTENT_TYPES } from '@/util/access_visibility'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import { useContentAccessStore } from '@/content_access/content_access.store.ts'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'

const props = defineProps({
  resource: {
    type: Object,
    required: true
  },
  condenseButtons: {
    type: Boolean,
    required: false,
    default: false
  },
  showTooltips: {
    type: Boolean,
    default: false
  }
})
const contentAccessStore = useContentAccessStore()
const { authInfo } = storeToRefs(useUserStore())
const { contentAccessStatus } = storeToRefs(contentAccessStore)

const downloadLinks = computed(() => {
  return props.resource.download_links || []
})

const googleDriveLinks = computed(() => {
  return props.resource.google_drive_links || []
})

const contentInfo = computed(() => {
  return {
    cu: props.resource.uuid,
    ct: props.resource.title,
    ctype: CONTENT_TYPES.RESOURCE
  }
})

const id = getCurrentInstance().uid

const emit = defineEmits(['resourceDownloadOpened', 'resourceDownloadClicked'])

const downloadPermissions = computed(() => {
  return actionPermissions(
    ACTION_TYPES.DOWNLOAD,
    CONTENT_TYPES.RESOURCE,
    props.resource,
    authInfo.value,
    contentAccessStatus.value
  )
})

const analytics = getAnalyticsTracker().getChildContext({
  resource_access: !downloadPermissions.value.block
})

const validateAndOpenDownload = (label, url, isGoogleDrive) => {
  let data = {
    download_url: url,
    label: label,
    is_google_drive_link: isGoogleDrive,
    isRedownload: contentAccessStatus.value.redownloadMap[props.resource.uuid] || false,
    resource_language: props.resource.language
  }
  if (props.resource['$_source']) {
    data.source = props.resource['$_source']
  }
  if (!downloadPermissions.value.block) {
    contentAccessStore.trackDownload({ resource: props.resource, link: url })
    analytics.sendEvent(EVENT_NAMES.RESOURCES.OPENED_DOWNLOAD_LINK, { ...data, ...contentInfo.value })
    emit('resourceDownloadOpened')
    window.open(url)
  }
}

const capitalizeLinkType = (linkType) => {
  return linkType.charAt(0).toUpperCase() + linkType.slice(1)
}

const formatLabelWithSet = (label) => {
  if (props.resource.set_number && props.resource.set_number !== 0) {
    // Insert " Set X" before the parentheses
    const parenIndex = label.lastIndexOf('(')
    if (parenIndex > 0) {
      const beforeParen = label.substring(0, parenIndex).trim()
      const afterParen = label.substring(parenIndex)
      return `${beforeParen} Set ${props.resource.set_number} ${afterParen}`
    } else {
      // No parentheses, just add to the end
      return `${label} Set ${props.resource.set_number}`
    }
  }
  return label
}

const downloadLinkItems = computed(() => {
  let items = []
  downloadLinks.value.forEach((link) => {
    const item = {
      label: 'Download ' + formatLabelWithSet(link.label),
      command: () => {
        validateAndOpenDownload(link.label, link.url, false)
      }
    }
    items.push(item)
  })
  return items
})

const googleDriveLinkItems = computed(() => {
  let items = []
  googleDriveLinks.value.forEach((link) => {
    const item = {
      label: formatLabelWithSet(link.label) + (link.link_type ? ' (' + capitalizeLinkType(link.link_type) + ')' : ''),
      command: () => {
        validateAndOpenDownload(link.label, link.url, true)
      }
    }
    items.push(item)
  })
  return items
})

const downloadBlockedButtonText = () => {
  return (
    (downloadPermissions.value.blockExpired && 'Membership expired') ||
    (downloadPermissions.value.blockQuota && 'Download quota exceeded')
  )
}
const downloadBlockedTooltipText = () => {
  if (downloadPermissions.value.blockExpired)
    return 'To download this resource, you must have an active membership through your school or district. Contact your administrator to purchase or renew your premium membership.'
  if (downloadPermissions.value.blockQuota)
    return `You have already used all of your available downloads for this month.`
}

const contentBlocked = () => {
  contentAccessStore.sendContentAccessBlockedQuota({
    actionType: ACTION_TYPES.DOWNLOAD,
    contentInfo: contentInfo
  })
}

const trackDownloadsShown = (event, links) => {
  if (!event) {
    analytics.sendEvent(EVENT_NAMES.RESOURCES.CLICKED_DOWNLOAD, {
      download_links: links.map((link) => link.label + '|' + link.url),
      ...contentInfo.value
    })
    emit('resourceDownloadClicked')
  }
}
</script>
<template>
  <div v-if="downloadLinks?.length || googleDriveLinks?.length" class="flex gap-2">
    <DropdownButton
      v-if="downloadLinks?.length && !downloadPermissions.block"
      :buttonAriaLabel="'Download ' + resource.title"
      :items="downloadLinkItems"
      buttonTheme="bluePrimary"
      buttonSize="small"
      :dropdownId="'download-dropdown-' + id"
      @dropdown:opened="($event) => trackDownloadsShown($event, downloadLinks)"
      :additionalClasses="condenseButtons ? '!p-1' : ''"
      :tooltipText="showTooltips ? 'Download' : ''"
    >
      <template #buttonContents>
        <span :class="['material-icons-outlined', condenseButtons ? 'text-[24px]' : 'text-lg']">file_download</span>
        <template v-if="!condenseButtons">
          Download
          <span class="material-icons-round text-lg">arrow_drop_down</span>
        </template>
      </template>
    </DropdownButton>
    <DropdownButton
      v-if="googleDriveLinks?.length && !downloadPermissions.block"
      :buttonAriaLabel="`Open with Google: ${resource.title} resource`"
      :items="googleDriveLinkItems"
      buttonTheme="blueOutlineSecondary"
      buttonSize="small"
      :dropdownId="'google-drive-dropdown-' + id"
      @dropdown:opened="($event) => trackDownloadsShown($event, googleDriveLinks)"
      :additionalClasses="condenseButtons ? '!p-1' : ''"
      :tooltipText="showTooltips ? 'Open with Google' : ''"
    >
      <template #buttonContents>
        <span :class="['material-icons-round', condenseButtons ? 'text-[24px]' : 'text-lg']">add_to_drive</span>
        <template v-if="!condenseButtons">
          Open With Google
          <span class="material-icons-round text-lg">arrow_drop_down</span>
        </template>
      </template>
    </DropdownButton>
    <template v-if="downloadPermissions.block">
      <GbButton
        v-if="!downloadPermissions.blockUnauthenticated && downloadLinks"
        gbTheme="bluePrimary"
        :tooltipText="downloadBlockedTooltipText()"
        @click="contentBlocked"
      >
        {{ downloadBlockedButtonText() }}
      </GbButton>
      <GbButton
        v-if="!downloadPermissions.blockUnauthenticated && googleDriveLinks"
        gbTheme="blueOutlineSecondary"
        :tooltipText="downloadBlockedTooltipText()"
        @click="contentBlocked"
      >
        <span class="material-icons-round pr-1 text-lg">add_to_drive</span>
        {{ downloadBlockedButtonText() }}
      </GbButton>
    </template>
  </div>
</template>
